import React from "react";
import { Layout, Button, Dropdown, Avatar, Space, Typography } from "antd";
import {
    BarChartOutlined,
    LoginOutlined,
    UserOutlined,
    WalletOutlined,
    LockOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";

const { Header, Content } = Layout;
const { Text } = Typography;

const handleLogout = () => {
    sessionStorage.removeItem("pending_mfa_token");
    sessionStorage.removeItem("pending_mfa_remember");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("refresh");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    window.location.href = "/login";
};

const getUser = () => {
    try {
        return JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    } catch {
        return {};
    }
};

type AgentPortalLayoutProps = {
    children: React.ReactNode;
};

const AgentPortalLayout = ({ children }: AgentPortalLayoutProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = getUser();

    const navItems = [
        {
            key: "/agent/settlements",
            label: "Settlements",
            icon: <WalletOutlined />,
            path: "/agent/settlements",
        },
        {
            key: "/agent/analytics",
            label: "Analytics",
            icon: <BarChartOutlined />,
            path: "/agent/analytics",
        },
    ];

    const accountItems = [
        {
            key: "profile",
            icon: <UserOutlined />,
            label: "Profile",
        },
        {
            key: "change-password",
            icon: <LockOutlined />,
            label: "Change Password",
        },
        {
            type: "divider" as const,
        },
        {
            key: "logout",
            icon: <LoginOutlined />,
            label: "Logout",
            danger: true,
        },
    ];

    const currentNavKey =
        navItems.find((item) => location.pathname.startsWith(item.path))?.key || "/agent/settlements";

    return (
        <Layout style={{ minHeight: "100vh", background: "#f5f7fb" }}>
            <Header
                style={{
                    height: 64,
                    background: "#111827",
                    padding: "0 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
            >
                <Link
                    to="/agent/settlements"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        color: "#fff",
                        textDecoration: "none",
                        minWidth: 0,
                    }}
                >
                    <img
                        src="/vertical-white.png"
                        alt="DAIA"
                        style={{
                            height: 38,
                            maxWidth: 90,
                            objectFit: "contain",
                        }}
                    />
                    <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>Agent Portal</div>
                        <div style={{ fontSize: 11, opacity: 0.75 }}>DAIA Premium Rooms</div>
                    </div>
                </Link>

                <Dropdown
                    menu={{
                        items: accountItems,
                        onClick: ({ key }) => {
                            if (key === "profile") {
                                navigate("/agent/profile");
                            }

                            if (key === "change-password") {
                                navigate("/agent/change-password");
                            }

                            if (key === "logout") {
                                handleLogout();
                            }
                        },
                    }}
                    placement="bottomRight"
                    trigger={["click"]}
                >
                    <Space style={{ cursor: "pointer", color: "#fff" }}>
                        <Avatar icon={<UserOutlined />} />
                        <span
                            style={{
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                            }}
                        >
                            {user?.first_name || user?.username || "Agent"}
                        </span>
                    </Space>
                </Dropdown>
            </Header>

            <div
                style={{
                    background: "#fff",
                    borderBottom: "1px solid #e5e7eb",
                    position: "sticky",
                    top: 64,
                    zIndex: 90,
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        maxWidth: 1120,
                        margin: "0 auto",
                        padding: "0 12px",
                    }}
                >
                    {navItems.map((item) => {
                        const active = currentNavKey === item.key;

                        return (
                            <Link
                                key={item.key}
                                to={item.path}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "14px 16px",
                                    color: active ? "#111827" : "#6b7280",
                                    borderBottom: active ? "3px solid #111827" : "3px solid transparent",
                                    fontWeight: active ? 700 : 500,
                                    textDecoration: "none",
                                }}
                            >
                                {item.icon}
                                {item.label}
                            </Link>
                        );
                    })}
                </div>
            </div>

            <Content
                style={{
                    width: "100%",
                    maxWidth: 1440,
                    margin: "0 auto",
                    padding: "16px",
                }}
            >
                <div
                    style={{
                        marginBottom: 16,
                    }}
                >
                    <Text type="secondary">
                        Welcome back{user?.first_name ? `, ${user.first_name}` : ""}.
                    </Text>
                </div>

                {children}
            </Content>
        </Layout>
    );
};

export default AgentPortalLayout;